/**
 * AcademicLibrary.jsx — new content kind sitting *under* Book Series.
 *
 * Feb 12 2026 user spec:
 *   *"Wire an academic Library under the Book series."*
 *   *"A new content Kind, will have, Book Title, Author name, year of
 *   publication, book price. Under Academic Library: We have Projects
 *   and Publications."*
 *
 * Layout: top tab pair (Projects · Publications), grid of cards
 * (cover thumb + title + author + year + price pill), bottom-right FAB
 * for Scribes / Kings / Admins to upload a new item.
 *
 * Routes used:
 *   GET  /api/academic-library?kind=project|publication
 *   POST /api/academic-library                         (upload)
 *   POST /api/academic-library/{id}/purchase           (unlock)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, GraduationCap, Plus, X as XIcon,
  Loader2, Lock, Check, Coins, Zap,
} from 'lucide-react';
import { authFetch, checkAuth } from '../utils/authUtils';
import FlashReadOverlay from '../components/FlashReadOverlay';
import AcademicQuizPrompt from '../components/AcademicQuizPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_TABS = [
  { key: 'project',     label: 'Projects',     Icon: GraduationCap },
  { key: 'publication', label: 'Publications', Icon: BookOpen },
];

const PricePill = ({ coins, paywalled, purchased }) => {
  if (purchased) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-[10px] font-bold uppercase tracking-widest">
        <Check className="w-3 h-3" /> Owned
      </span>
    );
  }
  if (coins === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 text-[10px] font-bold uppercase tracking-widest">
        Free
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${paywalled ? 'bg-amber-500/15 border-amber-400/30 text-amber-200' : 'bg-white/5 border-white/10 text-slate-200'} border text-[10px] font-bold uppercase tracking-widest`}>
      {paywalled ? <Lock className="w-3 h-3" /> : <Coins className="w-3 h-3" />}
      {coins.toLocaleString()} coins
    </span>
  );
};

const UploadModal = ({ open, onClose, onCreated, kindDefault }) => {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    kind: kindDefault || 'project',
    title: '',
    author_name: '',
    year_of_publication: new Date().getFullYear(),
    price_coins: 0,
    description: '',
    cover_image_url: '',
    pdf_url: '',
    text_content: '',
  });

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, kind: kindDefault || f.kind }));
  }, [open, kindDefault]);

  if (!open) return null;

  const onChange = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));

  const submit = async () => {
    setErr('');
    if (!form.title.trim()) { setErr('Title is required.'); return; }
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/academic-library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          year_of_publication: Number(form.year_of_publication) || 0,
          price_coins: Number(form.price_coins) || 0,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `Upload failed (${r.status})`); return; }
      onCreated && onCreated(d);
      onClose();
    } catch (e) {
      setErr(e?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="academic-upload-overlay" onClick={onClose}>
      <div
        className="w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl bg-[#0f1117] border border-white/10 max-h-[90vh] overflow-y-auto"
        onClick={(ev) => ev.stopPropagation()}
        data-testid="academic-upload-dialog"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
            New Academic item
          </p>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center" data-testid="academic-upload-close" aria-label="Close upload dialog">
            <XIcon className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {/* Kind */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Kind</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {KIND_TABS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, kind: key }))}
                  className={`px-3 py-2 rounded-xl border text-sm font-bold ${form.kind === key ? 'bg-amber-500 text-black border-amber-400' : 'bg-white/5 text-slate-300 border-white/10'}`}
                  data-testid={`academic-upload-kind-${key}`}
                >
                  {label.replace(/s$/, '')}
                </button>
              ))}
            </div>
          </div>
          {/* Title */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Book title *</label>
            <input value={form.title} onChange={onChange('title')} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40" placeholder="e.g. Introduction to Narrative Theory" data-testid="academic-upload-title" />
          </div>
          {/* Author */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Author name</label>
            <input value={form.author_name} onChange={onChange('author_name')} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40" placeholder="Defaults to your name" data-testid="academic-upload-author" />
          </div>
          {/* Year + Price */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Year of publication</label>
              <input type="number" value={form.year_of_publication} onChange={onChange('year_of_publication')} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40" data-testid="academic-upload-year" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Price (coins)</label>
              <input type="number" min="0" value={form.price_coins} onChange={onChange('price_coins')} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40" data-testid="academic-upload-price" />
            </div>
          </div>
          {/* Description */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Description</label>
            <textarea value={form.description} onChange={onChange('description')} rows={3} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40" placeholder="A short abstract / blurb" data-testid="academic-upload-description" />
          </div>
          {/* Cover URL */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Cover image URL (optional)</label>
            <input value={form.cover_image_url} onChange={onChange('cover_image_url')} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40" placeholder="https://…" data-testid="academic-upload-cover" />
          </div>
          {/* PDF URL */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">PDF / file URL (optional)</label>
            <input value={form.pdf_url} onChange={onChange('pdf_url')} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40" placeholder="https://…" data-testid="academic-upload-pdf" />
          </div>
          {/* Text content — required for the flash/chunk reader + quiz flow. */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
              Full text <span className="text-amber-300/70">(enables reader + quiz reward)</span>
            </label>
            <textarea
              value={form.text_content}
              onChange={onChange('text_content')}
              rows={6}
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
              placeholder="Paste the full text of the work. Readers earn 100 XP per 1k words + 2 coins per correct quiz answer."
              data-testid="academic-upload-text"
            />
          </div>
          {err && <p className="text-rose-300 text-xs" data-testid="academic-upload-err">{err}</p>}
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm disabled:opacity-60"
            data-testid="academic-upload-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ItemCard = ({ it, onPurchase, onRead, busy }) => {
  const paywalled = (it.price_coins || 0) > 0 && !it.purchased;
  return (
    <div className="rounded-2xl border border-white/8 bg-[#12131A] overflow-hidden flex flex-col" data-testid={`academic-item-${it.item_id}`}>
      <div className="aspect-[3/4] bg-gradient-to-br from-amber-500/30 to-fuchsia-500/20 relative">
        {it.cover_image_url ? (
          <img src={it.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <BookOpen className="w-12 h-12 text-amber-200/80" />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <PricePill coins={it.price_coins || 0} paywalled={paywalled} purchased={it.purchased} />
        </div>
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <p className="text-sm font-bold text-white line-clamp-2">{it.title}</p>
        <p className="text-[11px] text-slate-400 mt-1 line-clamp-1">{it.author_name || 'Unknown author'}</p>
        {it.year_of_publication ? (
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">
            {it.year_of_publication}
          </p>
        ) : null}
        <div className="mt-3 flex items-center gap-2">
          {paywalled ? (
            <button
              type="button"
              onClick={() => onPurchase(it)}
              disabled={busy}
              className="flex-1 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[11px] font-bold disabled:opacity-60 transition-colors"
              data-testid={`academic-item-purchase-${it.item_id}`}
            >
              Buy for {it.price_coins} coins
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onRead(it)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[11px] font-bold transition-colors"
                data-testid={`academic-item-read-${it.item_id}`}
              >
                <Zap className="w-3 h-3" /> Read & earn
              </button>
              {it.pdf_url && (
                <a
                  href={it.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[11px] font-bold transition-colors"
                  data-testid={`academic-item-pdf-${it.item_id}`}
                >
                  PDF
                </a>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AcademicLibrary = () => {
  const [user, setUser] = useState(null);
  const [kind, setKind] = useState('project');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const u = await checkAuth();
        if (alive) setUser(u || null);
      } catch (_) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  const canUpload = ['admin', 'coadmin', 'king', 'writer'].includes(user?.role || '');
  // Read-flow state: when set, FlashReadOverlay is rendered with the
  // item's text_content; when DONE we surface the retention-quiz prompt.
  const [readingItem, setReadingItem] = useState(null);
  const [quizItemId, setQuizItemId] = useState(null);

  const startReading = async (it) => {
    // Need the full item detail for `text_content`.
    try {
      const r = await authFetch(`${BACKEND_URL}/api/academic-library/${it.item_id}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `Could not open (${r.status})`); return; }
      if (!(d.text_content || d.description)) {
        setErr('This item has no readable text yet.');
        return;
      }
      setReadingItem(d);
    } catch (e) {
      setErr(e?.message || 'Network error');
    }
  };

  const onReaderClose = () => {
    // After flash/chunk read, surface the retention quiz.
    if (readingItem) setQuizItemId(readingItem.item_id);
    setReadingItem(null);
  };

  const fetchItems = async (k) => {
    setLoading(true);
    setErr('');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/academic-library?kind=${k}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `Could not load (${r.status})`); return; }
      setItems(d.items || []);
    } catch (e) {
      setErr(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(kind); }, [kind]);

  const purchase = async (it) => {
    setBusyId(it.item_id);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/academic-library/${it.item_id}/purchase`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(d.detail || `Purchase failed (${r.status})`);
        return;
      }
      // Optimistically mark as purchased.
      setItems((prev) => prev.map((x) => x.item_id === it.item_id ? { ...x, purchased: true } : x));
    } catch (e) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => items.filter((it) => it.kind === kind), [items, kind]);

  return (
    <main className="max-w-5xl mx-auto px-4 py-6 pb-32" data-testid="academic-library-page">
      {/* Crumb */}
      <Link
        to="/book-series"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-400 hover:text-white font-bold mb-3"
        data-testid="academic-library-crumb"
      >
        <ArrowLeft className="w-3 h-3" /> Back to Book Series
      </Link>

      {/* Header */}
      <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
        Academic Library<span className="text-amber-400">.</span>
      </h1>
      <p className="text-sm text-slate-400 mt-1">
        Projects, publications and study material — written by Scribes 🖋️
      </p>

      {/* Tabs */}
      <div className="mt-5 flex gap-2" role="tablist" data-testid="academic-library-tabs">
        {KIND_TABS.map(({ key, label, Icon }) => {
          const active = kind === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setKind(key)}
              className={`px-4 py-2 rounded-full border text-sm font-bold transition-colors inline-flex items-center gap-2 ${active ? 'bg-amber-500 text-black border-amber-400' : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'}`}
              data-testid={`academic-library-tab-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className="mt-6">
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="academic-library-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-white/8 bg-[#12131A] overflow-hidden animate-pulse">
                <div className="aspect-[3/4] bg-white/5" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-white/10 rounded w-3/4" />
                  <div className="h-2 bg-white/10 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}
        {err && !loading && (
          <p className="text-rose-300 text-sm" data-testid="academic-library-err">{err}</p>
        )}
        {!loading && !err && filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400" data-testid="academic-library-empty">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Nothing here yet.</p>
            {canUpload && (
              <p className="text-xs mt-1">
                Be the first — tap the <span className="text-amber-300 font-bold">+</span> button.
              </p>
            )}
          </div>
        )}
        {!loading && !err && filtered.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="academic-library-grid">
            {filtered.map((it) => (
              <ItemCard
                key={it.item_id}
                it={it}
                onPurchase={purchase}
                onRead={startReading}
                busy={busyId === it.item_id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upload FAB */}
      {canUpload && (
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="fixed bottom-24 right-5 sm:bottom-8 sm:right-8 z-40 w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-400 text-black grid place-items-center shadow-2xl shadow-amber-500/30 transition-transform hover:scale-105 active:scale-100"
          data-testid="academic-library-upload-fab"
          aria-label="Upload new academic item"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        kindDefault={kind}
        onCreated={() => fetchItems(kind)}
      />

      {/* Flash / chunk reader for academic content. The existing
          FlashReadOverlay supports BOTH modes via its chunk-size
          picker (1 word = flash, 2-3 words = chunk) and an in-player
          speed dial. When the user closes the reader we trigger the
          retention-quiz prompt so they can claim the coin/XP reward. */}
      {readingItem && (
        <FlashReadOverlay
          text={readingItem.text_content || readingItem.description || ''}
          title={readingItem.title}
          onClose={onReaderClose}
        />
      )}

      <AcademicQuizPrompt
        itemId={quizItemId}
        open={!!quizItemId}
        onClose={() => setQuizItemId(null)}
      />
    </main>
  );
};

export default AcademicLibrary;
